/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { addMonths } from "date-fns"
import _ from "lodash"
import { z } from "zod"

const preprocessToNumber = (val: unknown) => {
  if (typeof val === "string") {
    const trimmed = val.trim()
    if (trimmed === "") return val
    return Number(trimmed)
  }
  return val
}

const preprocessOptionalToNumber = (val: unknown) => {
  if (typeof val === "string") {
    const trimmed = val.trim()
    // Convert empty string to undefined so the optional check passes
    if (trimmed === "") return undefined
    return Number(trimmed)
  }
  return val
}

export const validNumber = z.preprocess(
  preprocessToNumber,
  z.number({
    invalid_type_error: "Please provide a valid number.",
    required_error: "This field is required.",
  })
)

export const validInteger = validNumber.refine(num => Number.isInteger(num), {
  message: "Please provide a whole number.",
})

export const validPositiveInteger = validInteger.refine(num => num >= 0, {
  message: "Please provide a number that is 0 or greater.",
})

export const validOptionalPositiveInteger = z.preprocess(
  preprocessOptionalToNumber,
  validPositiveInteger.optional()
)

const validPositiveFloat = validNumber.refine(num => num >= 0, {
  message: "Please provide a number that is 0 or greater.",
})

const validMonth = validPositiveInteger.refine(month => month >= 1 && month <= 12, {
  message: "Please provide a valid month.",
})

export const yearMonthSchema = z.object(
  {
    month: validMonth,
    year: validPositiveInteger,
  },
  {
    invalid_type_error: "Please provide a valid month and year.",
  }
)

const allocationSchema = z
  .object({
    spxTrPct: validNumber,
    worldExUsEquitiesPct: validNumber,
    cashPct: validNumber,
    bm10yPct: validNumber,
    bm30yPct: validNumber,
    commoditiesPct: validNumber,
    goldPct: validNumber,
  })
  .superRefine((allocations, ctx) => {
    const allocationSum = _.sum(_.values(allocations))
    if (allocationSum !== 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The sum of the allocation percentages must be 100 (it's currently ${allocationSum}).`,
        path: [],
      })
    }
  })

const famaFrenchFactorsSchema = z.object({
  smallStocksPct: validNumber,
  valueStocksPct: validNumber,
})

const assetsSchema = z.object({
  expenseRatioPerAnnumPct: validPositiveFloat,
  allocation: allocationSchema,
  famaFrenchFactors: famaFrenchFactorsSchema,
})

const cashFlowSchema = z
  .object({
    name: z.preprocess(
      val => (val === null ? "" : val),
      z.string().min(1, "Please provide a name.")
    ),
    amount: validInteger,
    frequency: z.string(),
    inflationAdjusted: z.preprocess(
      val => (val === null ? undefined : val),
      z.boolean({
        required_error: "Please provide a value.",
      })
    ),
    start: yearMonthSchema,
    end: yearMonthSchema.optional(),
  })
  .superRefine((cashFlow, ctx) => {
    if (!["annually", "monthly", "one-time"].includes(cashFlow.frequency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be "annually", "monthly", or "one-time", but received "${cashFlow.frequency}" instead.`,
        path: [cashFlow.name, "frequency"],
      })
    }
    if (cashFlow.frequency === "one-time") {
      if (cashFlow.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `An end date is not allowed for one-time cash flow ${cashFlow.name}.`,
          path: ["end"],
        })
      }
    } else if (!cashFlow.end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a valid month and year.",
        path: ["end"],
      })
    } else {
      const startValue = cashFlow.start.year * 12 + (cashFlow.start.month - 1)
      const endValue = cashFlow.end.year * 12 + (cashFlow.end.month - 1)
      if (startValue > endValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must equal or follow start date.",
          path: ["end"],
        })
      }
    }
  })

export const userParametersSchema = z
  .object({
    initialPortfolioDollars: validPositiveInteger,
    retirementStart: yearMonthSchema,
    retirementHorizonMonths: validPositiveInteger,
    finalValuePctOfInitial: validPositiveFloat,
    initialConsumptionPerAnnumDollars: validOptionalPositiveInteger,
    inflationPerAnnumPct: validPositiveFloat,
    cape: z.preprocess(
      val => val ?? "",
      z.enum(["shiller", "ern"], {
        errorMap: (issue, ctx) => {
          if (issue.code === "invalid_enum_value") {
            if (ctx.data === "") {
              return { message: `Please select "shiller" or "ern".` }
            }
            return { message: `Must be "shiller" or "ern", but received "${ctx.data}" instead.` }
          }
          return { message: ctx.defaultError }
        },
      })
    ),
    allocationMode: z.preprocess(
      val => val ?? "",
      z.enum(["static", "glide path"], {
        errorMap: (issue, ctx) => {
          if (issue.code === "invalid_enum_value") {
            if (ctx.data === "") {
              return {
                message: `Please select "static" or "glide path".`,
              }
            }
            return {
              message: `Must be "static" or "glide path", but received "${ctx.data}" instead.`,
            }
          }
          return { message: ctx.defaultError }
        },
      })
    ),
    glidePathLengthMonths: validPositiveInteger.optional(),
    initialAssets: assetsSchema,
    finalAssets: assetsSchema.optional(),
    cashFlows: z.array(cashFlowSchema),
  })
  .superRefine((data, ctx) => {
    const retirementStartValue = data.retirementStart.year * 12 + (data.retirementStart.month - 1)
    data.cashFlows.forEach((cashFlow, index) => {
      const cashFlowStartValue = cashFlow.start.year * 12 + (cashFlow.start.month - 1)
      if (cashFlowStartValue < retirementStartValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Cash flow cannot start before the retirement start date.",
          path: ["cashFlows", index, "start"],
        })
      }
      if (cashFlow.end) {
        const retirementEndValue = retirementStartValue + data.retirementHorizonMonths
        const cashFlowEndValue = cashFlow.end.year * 12 + (cashFlow.end.month - 1)
        if (cashFlowEndValue > retirementEndValue) {
          const retirementEnd = addMonths(
            new Date(data.retirementStart.year, data.retirementStart.month - 1, 1),
            data.retirementHorizonMonths
          )
          const retirementEndStr = `${retirementEnd.getMonth() + 1}/${retirementEnd.getFullYear()}`

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Cash flow cannot end after the retirement period, which ends ${retirementEndStr}.`,
            path: ["cashFlows", index, "end"],
          })
        }
      }
    })

    if (data.allocationMode === "glide path" && !data.glidePathLengthMonths) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a valid number.",
        path: ["glidePathLengthMonths"],
      })
    } else if (
      data.allocationMode === "glide path" &&
      data.glidePathLengthMonths != null &&
      data.glidePathLengthMonths > data.retirementHorizonMonths
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Glide path cannot be longer than retirement.",
        path: ["glidePathLengthMonths"],
      })
    } else if (data.allocationMode === "glide path" && !data.finalAssets) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Final assets are required when an asset allocation glide path is specified.",
        path: [],
      })
    } else if (data.allocationMode === "static" && data.glidePathLengthMonths) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Glide path length cannot be specified when a static asset allocation is specified.",
        path: [],
      })
    } else if (data.allocationMode === "static" && data.finalAssets) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Final assets cannot be specified when a static asset allocation is specified.",
        path: [],
      })
    }
  })

export type UserParameters = z.infer<typeof userParametersSchema>
export type CashFlow = z.infer<typeof cashFlowSchema>

export const initialUserParameters: UserParameters = {
  initialPortfolioDollars: 3000000,
  retirementStart: (() => {
    const today = new Date()
    return { month: today.getMonth() + 1, year: today.getFullYear() }
  })(),
  retirementHorizonMonths: 600,
  finalValuePctOfInitial: 0,
  initialConsumptionPerAnnumDollars: undefined,
  inflationPerAnnumPct: 2.5,
  cape: "ern",
  allocationMode: "static",
  initialAssets: {
    expenseRatioPerAnnumPct: 0.05,
    allocation: {
      cashPct: 0,
      spxTrPct: 80,
      bm10yPct: 20,
      bm30yPct: 0,
      worldExUsEquitiesPct: 0,
      commoditiesPct: 0,
      goldPct: 0,
    },
    famaFrenchFactors: {
      smallStocksPct: 0,
      valueStocksPct: 0,
    },
  },
  cashFlows: [],
}
